// Content brackets: the skill/quality filter shown on tier lists and other
// run-derived content. "All" and "Asc 10" map to existing run brackets; the
// win-rate tiers are an A10-gated quality ladder (A10 runs from players above
// that overall win rate). Keep the keys in sync with _BRACKET_KEYS in
// backend/app/services/run_entity_stats.py.
export interface ContentBracket {
  key: string;
  param: string | null; // value sent as ?bracket= to the API; null = all runs
  label: string;
}

export const CONTENT_BRACKETS: ContentBracket[] = [
  { key: "all", param: null, label: "All" },
  { key: "a10", param: "a10", label: "A10" },
  { key: "wr30", param: "wr30", label: "A10 >30% WR" },
  { key: "wr50", param: "wr50", label: "A10 >50% WR" },
  { key: "wr75", param: "wr75", label: "A10 >75% WR" },
];

// Exact player-count brackets. A separate axis from the content/skill brackets
// above (they share the single ?bracket= slot, so they're mutually exclusive),
// kept out of CONTENT_BRACKETS so the content-bracket pill rows and charts don't
// render them. Keys match _run_extra_brackets in run_entity_stats.py.
export const PLAYER_BRACKETS: ContentBracket[] = [
  { key: "solo", param: "solo", label: "Solo" },
  { key: "2p", param: "2p", label: "2P" },
  { key: "3p", param: "3p", label: "3P" },
  { key: "4p", param: "4p", label: "4P" },
];

// Game-mode brackets. Like the player axis they share the single ?bracket=
// slot: picking a mode replaces the player/skill selection (only the version
// composes on top). "All" is the absence of a mode key.
export const MODE_BRACKETS: ContentBracket[] = [
  { key: "standard", param: "standard", label: "Standard" },
  { key: "daily", param: "daily", label: "Daily" },
  { key: "custom", param: "custom", label: "Custom" },
];

export const CHARACTER_BRACKETS: ContentBracket[] = [
  { key: "ironclad", param: "ironclad", label: "Ironclad" },
  { key: "silent", param: "silent", label: "Silent" },
  { key: "defect", param: "defect", label: "Defect" },
  { key: "necrobinder", param: "necrobinder", label: "Necrobinder" },
  { key: "regent", param: "regent", label: "Regent" },
];

// Both axes are valid ?bracket= values, so normalizeBracket must recognize them.
const _BY_KEY = new Map(
  [
    ...CONTENT_BRACKETS,
    ...PLAYER_BRACKETS,
    ...MODE_BRACKETS,
    ...CHARACTER_BRACKETS,
  ].map((b) => [b.key, b]),
);
const _CHARACTER_KEYS = new Set(CHARACTER_BRACKETS.map((b) => b.key));

const _PLAYER_KEYS = new Set(PLAYER_BRACKETS.map((b) => b.key));
const _SKILL_KEYS = new Set(
  CONTENT_BRACKETS.filter((b) => b.key !== "all").map((b) => b.key),
);

/**
 * A game-version bracket ("v0.107.1"): the snapshot keeps a per-version
 * slice for every release version, both standalone and composed onto any
 * other bracket key ("solo:wr50:v0.107.1"), so versions combine with the
 * player and skill axes everywhere.
 */
export function isVersionBracket(raw: string | undefined | null): boolean {
  return !!raw && /^v\d+(\.\d+)*(-rc\.\d+)?$/.test(raw);
}

/** The bracket value minus any trailing version segment ("" for a bare
 * version or "all"). */
export function stripVersion(raw: string | undefined | null): string {
  if (!raw || raw === "all") return "";
  const { base } = splitVersion(raw);
  return base === "all" ? "" : base;
}

/** Split a trailing ":vX.Y.Z" version segment off a bracket value. */
function splitVersion(raw: string): { base: string; version: string } {
  const i = raw.lastIndexOf(":");
  if (i > 0 && isVersionBracket(raw.slice(i + 1))) {
    return { base: raw.slice(0, i), version: raw.slice(i + 1) };
  }
  if (isVersionBracket(raw)) return { base: "", version: raw };
  return { base: raw, version: "" };
}

const _MODE_KEYS = new Set(MODE_BRACKETS.map((b) => b.key));

/**
 * A composite combines distinct axes with colons: player + skill
 * ("solo:wr50"), and -- on pages whose data source folds a bracket cube
 * (community stats) -- game mode as a third axis ("standard:solo:wr50").
 * Each axis at most once, two or more parts, any order.
 */
export function isCompositeBracket(raw: string | undefined | null): boolean {
  if (!raw || !raw.includes(":")) return false;
  const parts = raw.split(":");
  if (parts.length < 2 || parts.length > 4) return false;
  let player = 0,
    skill = 0,
    mode = 0,
    character = 0;
  for (const part of parts) {
    if (_PLAYER_KEYS.has(part)) player++;
    else if (_SKILL_KEYS.has(part)) skill++;
    else if (_MODE_KEYS.has(part)) mode++;
    else if (_CHARACTER_KEYS.has(part)) character++;
    else return false;
  }
  return player <= 1 && skill <= 1 && mode <= 1 && character <= 1;
}

/** Split a bracket value into its player + skill + mode + version axes. A
 * single bracket maps to whichever axis owns it; "all"/unknown gives all
 * empty. */
export function splitBracket(raw: string | undefined | null): {
  player: string;
  skill: string;
  mode: string;
  character: string;
  version: string;
} {
  const b = normalizeBracket(raw);
  const { base, version } = splitVersion(b === "all" ? "" : b);
  let player = "",
    skill = "",
    mode = "",
    character = "";
  for (const part of base ? base.split(":") : []) {
    if (_PLAYER_KEYS.has(part)) player = part;
    else if (_SKILL_KEYS.has(part)) skill = part;
    else if (_MODE_KEYS.has(part)) mode = part;
    else if (_CHARACTER_KEYS.has(part)) character = part;
  }
  return { player, skill, mode, character, version };
}

/** Combine player + skill + mode + version selections into one ?bracket=
 * value. Any subset works: the axes compose in canonical
 * player:skill:mode:version order, and all-empty collapses to "all". */
export function combineBracket(
  player: string,
  skill: string,
  version = "",
  mode = "",
  character = "",
): string {
  const base = [player, skill, mode, character].filter(Boolean).join(":");
  if (base && version) return `${base}:${version}`;
  return base || version || "all";
}

/** The cohort a stats page shows when its URL carries no ?bracket=: standard
 * solo runs. "all" must be asked for explicitly. */
export const DEFAULT_CONTENT_BRACKET = "solo:standard";

/** The default on surfaces whose data has no game-mode axis (encounter
 * stats, the entity-page bracket pills). */
export const DEFAULT_SOLO_BRACKET = "solo";

/** The bracket a page should use for a raw ?bracket= value: the page default
 * when the URL carries none, else the normalized value. */
export function defaultBracket(
  raw: string | undefined | null,
  fallback = DEFAULT_CONTENT_BRACKET,
): string {
  return raw ? normalizeBracket(raw) : fallback;
}

/** The ?bracket= value to write into a page URL: nothing for the page
 * default (the URL stays clean), the explicit key otherwise, "all" included. */
export function bracketQuery(
  bracket: string | undefined | null,
  fallback = DEFAULT_CONTENT_BRACKET,
): string | null {
  const n = normalizeBracket(bracket);
  return n === fallback ? null : n;
}

/** Normalize a raw ?bracket= value to a known bracket key, a player:skill
 * composite, a game version, or any of those with a trailing version
 * ("all" if unknown). */
export function normalizeBracket(raw: string | undefined | null): string {
  if (!raw) return "all";
  if (_BY_KEY.has(raw)) return raw;
  if (isCompositeBracket(raw)) return raw;
  if (isVersionBracket(raw)) return raw;
  const { base, version } = splitVersion(raw);
  if (version && base && (_BY_KEY.has(base) || isCompositeBracket(base)))
    return raw;
  return "all";
}

/** The ?bracket= API value for a bracket key (null = all runs, no param). A
 * composite passes through unchanged (its API value is the composite itself). */
export function bracketParam(key: string | undefined | null): string | null {
  const n = normalizeBracket(key);
  if (n === "all") return null;
  if (isCompositeBracket(n) || isVersionBracket(n)) return n;
  // base+version combos ("wr50:v0.111.0", "solo:wr50:v0.111.0") are already
  // the API value; the map lookup below only resolves plain single-axis keys
  // (and used to swallow these into null = all runs).
  const { base, version } = splitVersion(n);
  if (version) return base === "all" ? version : n;
  return _BY_KEY.get(n)?.param ?? null;
}

/**
 * /api/runs/list filters for a bracket, for the run-browse table (which has no
 * precomputed brackets and filters per run instead). Mirrors the bracket
 * definition: the win-rate tiers are A10-gated, so they pin ascension_min=10
 * and add a per-submitter winrate floor (0-100). "All" sends nothing.
 */
export function bracketListParams(key: string | undefined | null): {
  ascension_min?: number;
  winrate_min?: number;
} {
  // Only the skill axis maps to list filters; a composed player/version
  // segment rides on its own params (players= / build_id=) at the callers.
  switch (splitBracket(key).skill || normalizeBracket(key)) {
    case "a10":
      return { ascension_min: 10 };
    case "wr30":
      return { ascension_min: 10, winrate_min: 30 };
    case "wr50":
      return { ascension_min: 10, winrate_min: 50 };
    case "wr75":
      return { ascension_min: 10, winrate_min: 75 };
    default:
      return {};
  }
}
